import type { ClientProposal } from '@hemmafixa/shared';
import { Button, FormField, InlinePanel, InlinePanelFooter, Textarea, cn, toast } from '@hemmafixa/ui';
import { useState, type FormEvent } from 'react';
import { fill, useI18n } from '@/i18n/context';
import { useCloseJob, useJobProposals } from './api';

const CANCEL = 'cancel';

/**
 * Closes the client's job (P5-05): give it to one of the accepted proposals, or cancel it. Before the job is
 * open only cancelling is possible. The other providers are told by the API. Opens in place of the Close button.
 */
export function CloseJobPanel({ jobId, onClose }: { jobId: string; onClose: () => void }) {
  const { t, money } = useI18n();
  const c = t.client.job;
  const proposals = useJobProposals(jobId);
  const accepted = (proposals.data ?? []).filter((proposal) => proposal.status === 'accepted');
  const close = useCloseJob(jobId);
  // An accepted proposal is preselected; cancelling is the one that cannot be undone, so it is never the default
  const [picked, setPicked] = useState<string | null>(null);
  const choice = picked ?? accepted[0]?.id ?? CANCEL;
  const setChoice = setPicked;
  const [reason, setReason] = useState('');
  const cancelling = choice === CANCEL;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    close.mutate(cancelling ? { reason: reason.trim() || undefined } : { proposalId: choice }, {
      onSuccess: () => {
        onClose();
        toast.success(cancelling ? c.cancelledToast : c.chosenToast);
      },
      onError: () => toast.error(c.closeFailed),
    });
  };

  const option = (value: string, label: string, detail?: string) => (
    <label
      key={value}
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm transition-colors',
        choice === value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/60',
      )}
    >
      <input
        type="radio"
        name="close-choice"
        value={value}
        checked={choice === value}
        onChange={() => setChoice(value)}
        className="mt-0.5 accent-[hsl(var(--primary))]"
      />
      <span className="grid gap-0.5">
        <span className="font-medium">{label}</span>
        {detail && <span className="text-muted-foreground">{detail}</span>}
      </span>
    </label>
  );

  const price = (proposal: ClientProposal) =>
    proposal.priceToOre
      ? `${money(proposal.priceFromOre)} – ${money(proposal.priceToOre)}`
      : money(proposal.priceFromOre);

  return (
    <InlinePanel
      title={c.closeTitle}
      description={c.closeText}
      onClose={onClose}
      onSubmit={submit}
      closeLabel={t.common.cancel}
    >
      <fieldset className="grid gap-2">
        <legend className="mb-2 text-sm font-medium">{c.closeHow}</legend>
        {accepted.map((proposal) =>
          option(
            proposal.id,
            fill(c.chooseProvider, { company: proposal.provider.companyName }),
            price(proposal),
          ),
        )}
        {option(CANCEL, c.cancelJob, accepted.length === 0 ? c.cancelOnlyText : undefined)}
      </fieldset>
      {cancelling && (
        <FormField label={c.cancelReason} hint={c.cancelReasonHint}>
          <Textarea
            rows={2}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </FormField>
      )}
      <InlinePanelFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t.common.cancel}
        </Button>
        <Button type="submit" variant={cancelling ? 'danger' : 'primary'} loading={close.isPending}>
          {cancelling ? c.cancelJob : c.closeJob}
        </Button>
      </InlinePanelFooter>
    </InlinePanel>
  );
}
